import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import LocalStorageService from 'services/LocalStorageService';
import {
  LOCAL_STORAGE_ACCESS_TOKEN,
  LOCAL_STORAGE_REFRESH_TOKEN,
} from 'services/api/constants';
import { setIsLoggedIn } from 'store/User/actions/user';
import { setAppMode } from 'store/Common/actions/common';
import { AppMode } from 'store/Common/reducers/common';
import { CompanyRole } from 'types/Company.types';
import { getLogoNavigationDestinationBasedOnAppMode } from 'components/Layout/Header/helpers';
import { decodeTokenAndSetUserData } from 'pages/Auth/LoginPage/helpers';

interface TokenData {
  access_token: string;
  refresh_token: string;
}

/**
 * Custom hook to handle successful login authentication
 * Manages token storage, user data decoding, app mode setting, and navigation
 *
 * @param tokenData - Object containing access_token and refresh_token from successful login
 */
export const useHandleLoginSuccess = (
  tokenData: TokenData | null | undefined
) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!tokenData) {
      return;
    }

    const selectedCompany = decodeTokenAndSetUserData(
      tokenData.access_token,
      dispatch
    );

    // Set tokens and logged in state immediately after successful login
    LocalStorageService.setItem(
      LOCAL_STORAGE_ACCESS_TOKEN,
      tokenData.access_token
    );
    LocalStorageService.setItem(
      LOCAL_STORAGE_REFRESH_TOKEN,
      tokenData.refresh_token
    );
    dispatch(setIsLoggedIn(true));

    // Only proceed with company selection if we have a company
    if (selectedCompany) {
      dispatch(
        setAppMode(
          selectedCompany.role === CompanyRole.SELLER
            ? AppMode.SUPPLIER
            : AppMode.BUYER
        )
      );
      navigate(
        getLogoNavigationDestinationBasedOnAppMode(
          selectedCompany.role === CompanyRole.SELLER
            ? AppMode.SUPPLIER
            : AppMode.BUYER
        )
      );
    }
  }, [tokenData, dispatch, navigate]);
};
